"use client";

import * as React from "react";
import { Bookmark, Gift, MessageSquare, Share2, Smile } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { GiftPopoverContent } from "@/components/shared/article/gift-popover";
import { ReactionPopoverContent } from "@/components/shared/article/reaction-popover";
import { SharePopoverContent } from "@/components/shared/article/share-popover";
import {
  FontScaleBootstrap,
  FontSizePopoverContent,
} from "@/components/shared/article/font-size-popover";
import { ArticleCommentDrawer } from "@/components/shared/article/comment-drawer";
import { useToggleToast } from "@/lib/toast";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";

const STORAGE_KEY = "hkej-bookmark-ejhealth-article";

/**
 * Horizontal reader-rail used inside the article body. Each affordance is a
 * 44px circular outlined button (EJ Health primary on hover/active) with a
 * tiny label below — same visual language as before. Functionality now
 * mirrors the v2 main-site article rail by reusing its popover content
 * components.
 */
export function ShareRailHorizontal() {
  return (
    <div className="flex flex-col gap-3">
      <FontScaleBootstrap />
      <div
        className="flex items-start gap-3 md:gap-x-4  overflow-x-auto no-scrollbar  py-0.5 flex-wrap lg:overflow-x-visible lg:gap-x-6 lg:gap-y-4"
        role="toolbar"
        aria-label="文章互動"
      >
        <ShareButton />
        <GiftButton />
        <BookmarkButton />
        <FontSizeButton />
        <ReactionButton />
        <CommentButton />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Shared visual atom — 44px round outlined button + label.
   ────────────────────────────────────────────────────────
   Uses forwardRef + props spread so Radix `<PopoverTrigger asChild>` and the
   CommentDrawer trigger Slot can wire their onClick / ref / data-state into
   the underlying <button>. Without this, the popovers would not toggle.
*/
type RailButtonProps = React.ComponentPropsWithoutRef<"button"> & {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
};

const RailButton = React.forwardRef<HTMLButtonElement, RailButtonProps>(
  function RailButton({ icon, label, active, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        {...rest}
        className={`group flex shrink-0 flex-col items-center gap-1.5 ${rest.className ?? ""}`}
      >
        <span
          className="inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors"
          style={{
            border: `1px solid ${
              active
                ? EJHEALTH_COLORS.primary
                : EJHEALTH_COLORS.dividerStrong
            }`,
            backgroundColor: active ? EJHEALTH_COLORS.primary : "transparent",
            color: active
              ? EJHEALTH_COLORS.textOnDark
              : EJHEALTH_COLORS.dark,
          }}
          onMouseEnter={(e) => {
            if (active) return;
            e.currentTarget.style.borderColor = EJHEALTH_COLORS.primary;
            e.currentTarget.style.color = EJHEALTH_COLORS.primary;
          }}
          onMouseLeave={(e) => {
            if (active) return;
            e.currentTarget.style.borderColor = EJHEALTH_COLORS.dividerStrong;
            e.currentTarget.style.color = EJHEALTH_COLORS.dark;
          }}
        >
          {icon}
        </span>
        <span
          className="whitespace-nowrap"
          style={{
            ...EJHEALTH_TYPE.meta,
            color: EJHEALTH_COLORS.textMuted,
            fontSize: 11,
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </span>
      </button>
    );
  },
);

/* ────────────────────────────────────────────────────────
   Individual triggers
   ──────────────────────────────────────────────────────── */
function ShareButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <RailButton
          icon={<Share2 className="h-[18px] w-[18px]" />}
          label="分享"
          active={open}
        />
      </PopoverTrigger>
      <SharePopoverContent
        placement="header"
        onClose={() => setOpen(false)}
      />
    </Popover>
  );
}

function GiftButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <RailButton
          icon={<Gift className="h-[18px] w-[18px]" />}
          label="贈送"
          active={open}
        />
      </PopoverTrigger>
      <GiftPopoverContent placement="header" onClose={() => setOpen(false)} />
    </Popover>
  );
}

function BookmarkButton() {
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    try {
      setSaved(window.localStorage.getItem(STORAGE_KEY) === "1");
    } catch {}
  }, []);

  const notify = useToggleToast();

  const toggle = () => {
    // `next` computed OUTSIDE the updater, and the toast raised outside it:
    // React runs updaters twice under StrictMode, so a side effect inside one
    // fires twice. `lib/toast.ts` documents the same trap.
    const next = !saved;
    setSaved(next);
    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, "1");
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {}
    notify.bookmark(next);
  };

  return (
    <RailButton
      icon={
        <Bookmark
          className="h-[18px] w-[18px]"
          fill={saved ? "currentColor" : "none"}
        />
      }
      label={saved ? "已收藏" : "收藏"}
      active={saved}
      onClick={toggle}
      aria-label={saved ? "已收藏" : "收藏"}
    />
  );
}

function FontSizeButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <RailButton
          icon={<span style={{ fontSize: 14, fontWeight: 700 }}>Aa</span>}
          label="字型"
          active={open}
        />
      </PopoverTrigger>
      <FontSizePopoverContent placement="header" />
    </Popover>
  );
}

function ReactionButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <RailButton
          icon={<Smile className="h-[18px] w-[18px]" />}
          label="心情"
          active={open}
        />
      </PopoverTrigger>
      <ReactionPopoverContent placement="header" />
    </Popover>
  );
}

function CommentButton() {
  return (
    <ArticleCommentDrawer
      count={12}
      trigger={
        <RailButton
          icon={<MessageSquare className="h-[18px] w-[18px]" />}
          label="留言"
        />
      }
    />
  );
}
